<script>
import FormFieldItem from './FormFieldItem.vue';
import { STAGE_TYPE_TEMPLATES, FIELD_TYPES } from 'dashboard/constants/stageFormTemplates';

// Contador para generar UIDs únicos
let uidCounter = 0;

function generateUid() {
  uidCounter += 1;
  return `uid_${Date.now()}_${uidCounter}_${Math.random().toString(36).substr(2, 9)}`;
}

export default {
  name: 'StageFormBuilder',
  components: {
    FormFieldItem,
  },
  props: {
    modelValue: {
      type: Object,
      default: () => ({ enabled: false, fields: [] }),
    },
    stageType: {
      type: String,
      required: true,
    },
  },
  emits: ['update:modelValue'],
  data() {
    return {
      fieldTypes: FIELD_TYPES,
    };
  },
  computed: {
    localConfig: {
      get() {
        return this.modelValue || { enabled: false, fields: [] };
      },
      set(value) {
        this.$emit('update:modelValue', value);
      },
    },
    fields: {
      get() {
        return this.localConfig.fields || [];
      },
      set(value) {
        // Asegurar que todos los fields tengan _uid
        const fieldsWithUid = value.map(field => ({
          ...field,
          _uid: field._uid || generateUid(),
        }));
        this.localConfig = { ...this.localConfig, fields: fieldsWithUid };
      },
    },
    hasTemplate() {
      return this.stageType && this.stageType !== 'regular' && STAGE_TYPE_TEMPLATES[this.stageType];
    },
    templateName() {
      const names = {
        won: 'Ganado',
        lost: 'Perdido',
        paused: 'Pausado',
        canceled: 'Cancelado',
      };
      return names[this.stageType] || this.stageType;
    },
  },
  methods: {
    loadTemplate() {
      if (!this.hasTemplate) return;
      
      const template = STAGE_TYPE_TEMPLATES[this.stageType];
      // Deep copy para evitar mutaciones del template original
      const templateCopy = JSON.parse(JSON.stringify(template));
      
      // Agregar _uid a cada field del template si no lo tiene
      if (templateCopy.fields) {
        templateCopy.fields = templateCopy.fields.map(field => ({
          ...field,
          _uid: field._uid || generateUid(),
        }));
      }
      
      this.$emit('update:modelValue', templateCopy);
    },
    addNewField() {
      const newField = {
        _uid: generateUid(),
        key: `campo_${Date.now()}`,
        type: 'text',
        label: 'Nuevo campo',
        required: false,
        placeholder: '',
      };
      
      this.fields = [...this.fields, newField];
    },
    updateField(index, updatedField) {
      const newFields = [...this.fields];
      newFields[index] = updatedField;
      this.fields = newFields;
    },
    removeField(index) {
      this.fields = this.fields.filter((_, i) => i !== index);
    },
    moveFieldUp(index) {
      if (index === 0) return;
      const newFields = [...this.fields];
      [newFields[index - 1], newFields[index]] = [newFields[index], newFields[index - 1]];
      this.fields = newFields;
    },
    moveFieldDown(index) {
      if (index === this.fields.length - 1) return;
      const newFields = [...this.fields];
      [newFields[index], newFields[index + 1]] = [newFields[index + 1], newFields[index]];
      this.fields = newFields;
    },
    clearAll() {
      if (confirm('¿Estás seguro de eliminar todos los campos?')) {
        this.fields = [];
      }
    },
  },
};
</script>

<template>
  <div class="stage-form-builder">
    <!-- Header con botones de template -->
    <div class="mb-4">
      <div class="flex items-center justify-between mb-2">
        <label class="block text-sm font-medium text-n-slate-12">
          Configuración de campos del formulario
        </label>
        <button
          v-if="hasTemplate"
          type="button"
          @click="loadTemplate"
          class="px-3 py-1.5 text-xs font-medium text-n-brand border border-n-brand rounded-md hover:bg-n-brand/5 transition-colors"
        >
          <fluent-icon icon="arrow-download" size="14" class="inline mr-1" />
          Cargar template "{{ templateName }}"
        </button>
      </div>
      
      <p class="text-xs text-n-slate-11">
        Configura los campos que aparecerán cuando se mueva un deal a esta etapa.
        Puedes cargar un template predefinido o crear campos personalizados.
      </p>
    </div>

    <!-- Lista de campos -->
    <div v-if="fields.length > 0" class="space-y-3 mb-4">
      <FormFieldItem
        v-for="(field, index) in fields"
        :key="field._uid || field.key || index"
        :field="field"
        :index="index"
        :all-fields="fields"
        @update:field="updateField(index, $event)"
        @remove="removeField(index)"
        @move-up="moveFieldUp(index)"
        @move-down="moveFieldDown(index)"
      />
    </div>

    <!-- Empty state -->
    <div
      v-else
      class="border-2 border-dashed border-n-weak rounded-lg p-8 text-center"
    >
      <div class="text-n-slate-11 mb-4">
        <fluent-icon icon="form" size="48" />
      </div>
      <p class="text-sm text-n-slate-12 font-medium mb-1">
        No hay campos configurados
      </p>
      <p class="text-xs text-n-slate-11 mb-4">
        {{ hasTemplate ? 'Carga un template o agrega campos manualmente' : 'Agrega campos para crear el formulario' }}
      </p>
      <div class="flex items-center justify-center gap-2">
        <button
          v-if="hasTemplate"
          type="button"
          @click="loadTemplate"
          class="px-4 py-2 text-sm font-medium text-white bg-n-brand rounded-md hover:bg-n-brand/90 transition-colors"
        >
          <fluent-icon icon="arrow-download" size="16" class="inline mr-1" />
          Cargar template "{{ templateName }}"
        </button>
        <button
          type="button"
          @click="addNewField"
          class="px-4 py-2 text-sm font-medium text-n-brand border border-n-brand rounded-md hover:bg-n-brand/5 transition-colors"
        >
          <fluent-icon icon="add" size="16" class="inline mr-1" />
          Crear campo personalizado
        </button>
      </div>
    </div>

    <!-- Botones de acción -->
    <div v-if="fields.length > 0" class="flex items-center justify-between pt-4 border-t border-n-weak">
      <button
        type="button"
        @click="addNewField"
        class="px-4 py-2 text-sm font-medium text-n-brand border border-n-brand rounded-md hover:bg-n-brand/5 transition-colors"
      >
        <fluent-icon icon="add" size="16" class="inline mr-1" />
        Agregar campo
      </button>

      <button
        type="button"
        @click="clearAll"
        class="px-3 py-2 text-sm font-medium text-n-ruby-10 hover:bg-n-ruby-9/10 rounded-md transition-colors"
      >
        <fluent-icon icon="delete" size="16" class="inline mr-1" />
        Limpiar todo
      </button>
    </div>

    <!-- Resumen -->
    <div v-if="fields.length > 0" class="mt-4 p-3 bg-n-slate-9/5 rounded-lg">
      <div class="flex items-start gap-2">
        <fluent-icon icon="info" size="16" class="text-n-brand mt-0.5" />
        <div class="flex-1">
          <p class="text-xs text-n-slate-12 font-medium">
            {{ fields.length }} campo{{ fields.length !== 1 ? 's' : '' }} configurado{{ fields.length !== 1 ? 's' : '' }}
          </p>
          <p class="text-xs text-n-slate-11 mt-1">
            {{ fields.filter(f => f.required).length }} obligatorio{{ fields.filter(f => f.required).length !== 1 ? 's' : '' }}
            •
            {{ fields.filter(f => f.updates_deal_field).length }} actualiza{{ fields.filter(f => f.updates_deal_field).length !== 1 ? 'n' : '' }} el Deal
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Estilos adicionales si es necesario */
</style>
